import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table2, FileText, BarChart3, ArrowUpRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOutputFiles } from '@/hooks/useOutputFiles';
import type { OutputFileDetail } from '@/types/outputFiles';

interface Props { connectorType: string; executionId: string; }

function basename(p: string): string {
  const segs = p.split('/').filter(Boolean);
  return segs.length ? segs[segs.length - 1] : p;
}

function formatBytes(b?: number | null): string {
  if (!b) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function datasetLabel(f: OutputFileDetail): string {
  return f.dataset_name || basename(f.path);
}

function datasetHref(f: OutputFileDetail): string {
  return f.dataset_id
    ? `/datasets/${f.dataset_id}`
    : `/datasets?search=${encodeURIComponent(datasetLabel(f))}`;
}

type Schema = NonNullable<OutputFileDetail['schema']>;

interface Group {
  key: string;
  label: string;
  href: string;
  files: OutputFileDetail[];
  rows: number;
  bytes: number;
}

/**
 * Groups an execution's output files by dataset. A dataset can hold many files
 * (e.g. one per ad account), so the file list is collapsible + scrollable.
 *
 * Schema is intentionally NOT part of the group: the primary output-files fetch
 * is made without schema (fast first paint), and schema is loaded lazily the
 * first time any dataset's Schema panel is expanded — see `schemaByDataset`.
 */
function groupByDataset(files: OutputFileDetail[]): Group[] {
  const map = new Map<string, Group>();
  for (const f of files) {
    const key = f.dataset_id || datasetLabel(f);
    let g = map.get(key);
    if (!g) {
      g = { key, label: datasetLabel(f), href: datasetHref(f), files: [], rows: 0, bytes: 0 };
      map.set(key, g);
    }
    g.files.push(f);
    g.rows += f.row_count ?? 0;
    g.bytes += f.size_bytes ?? 0;
  }
  return [...map.values()];
}

/**
 * Builds a dataset-key → schema map from the schema-bearing (lazy) fetch.
 * Schema is a property of the dataset — one schema shared by all of its files —
 * so we take the first non-empty schema seen for each dataset key.
 */
function schemaByDataset(files: OutputFileDetail[]): Map<string, Schema> {
  const map = new Map<string, Schema>();
  for (const f of files) {
    const key = f.dataset_id || datasetLabel(f);
    if (f.schema?.length && !map.get(key)?.length) map.set(key, f.schema);
  }
  return map;
}

export function DatasetOutputGroups({ connectorType, executionId }: Props) {
  // First paint uses metadata only (no schema): the schema payload is otherwise
  // duplicated across every file and requires a per-file parquet-footer read.
  const { data, isLoading, error } = useOutputFiles(connectorType, executionId, { include_schema: false });

  // Schema is fetched lazily — enabled only once the user expands any Schema
  // panel. It's one execution-wide fetch (RQ-cached), so the first expand loads
  // schema for every group and subsequent expands are instant.
  const [schemaRequested, setSchemaRequested] = useState(false);
  const { data: schemaData, isFetching: schemaFetching } = useOutputFiles(
    connectorType,
    executionId,
    { include_schema: true },
    schemaRequested
  );

  const groups = useMemo(() => groupByDataset(data?.files ?? []), [data]);
  const schemaMap = useMemo(() => schemaByDataset(schemaData?.files ?? []), [schemaData]);

  if (isLoading) return <div className="py-3 text-sm text-muted-foreground">Loading datasets…</div>;
  if (error) return <div className="py-3 text-sm text-destructive">{(error as Error).message}</div>;
  if (!groups.length) return <div className="py-3 text-sm text-muted-foreground">No datasets updated by this run.</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Datasets updated
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold normal-case text-muted-foreground">
          {groups.length}
        </span>
      </div>
      {groups.map((g) => (
        <DatasetGroup
          key={g.key}
          group={g}
          schema={schemaMap.get(g.key)}
          schemaLoading={schemaRequested && schemaFetching && !schemaData}
          onRequestSchema={() => setSchemaRequested(true)}
        />
      ))}
    </div>
  );
}

function DatasetGroup({
  group,
  schema,
  schemaLoading,
  onRequestSchema,
}: {
  group: Group;
  schema?: Schema;
  schemaLoading: boolean;
  onRequestSchema: () => void;
}) {
  const [showSchema, setShowSchema] = useState(false);
  const columns = schema ?? [];
  const [showFiles, setShowFiles] = useState(false);
  const [filter, setFilter] = useState('');

  // Distinct per-account partitions (the leaf segment of each file path), used
  // to surface "N accounts" for multi-account connectors.
  // A DuckLake dataset's entries are MERGES (one staged file merged each); the
  // table's physical file count is the Files tile's job (#2235). Name the unit
  // so this row cannot read as a second, disagreeing "files" figure.
  const unit = group.files.every((f) => f.format === 'ducklake_table')
    ? { one: 'merge', many: 'merges' }
    : { one: 'file', many: 'files' };
  const accounts = new Set(group.files.filter((f) => f.dataset_id).map((f) => basename(f.path))).size;
  const q = filter.trim().toLowerCase();
  const filtered = q
    ? group.files.filter((f) => f.filename.toLowerCase().includes(q) || f.path.toLowerCase().includes(q))
    : group.files;

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex items-center gap-3 p-3">
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-primary/10 text-primary">
          <Table2 className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <Link
            to={group.href}
            className="group inline-flex items-center gap-1 font-mono text-[13.5px] font-semibold hover:text-primary hover:underline"
          >
            {group.label}
            <ArrowUpRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
          </Link>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {group.rows.toLocaleString()} rows · {formatBytes(group.bytes)} ·{' '}
            {group.files.length} {group.files.length === 1 ? unit.one : unit.many}
            {accounts > 1 ? ` · ${accounts} accounts` : ''}
            {columns.length ? ` · ${columns.length} columns` : ''}
          </div>
        </div>
        <div className="flex flex-none gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onRequestSchema();
              setShowSchema((s) => !s);
            }}
            aria-label={`Toggle schema for ${group.label}`}
          >
            <BarChart3 className="mr-1 h-4 w-4" aria-hidden /> Schema
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowFiles((s) => !s)} aria-label={`Toggle ${unit.many} for ${group.label}`}>
            <FileText className="mr-1 h-4 w-4" aria-hidden /> {unit.many[0].toUpperCase() + unit.many.slice(1)} ({group.files.length})
          </Button>
        </div>
      </div>

      {showSchema && (
        <div className="border-t bg-background px-3.5 py-3">
          <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
            Schema{columns.length ? ` · ${columns.length} columns` : ''}
          </div>
          {schemaLoading ? (
            <div className="text-xs text-muted-foreground">Loading schema…</div>
          ) : columns.length ? (
            columns.map((c) => (
              <div key={c.name} className="flex justify-between border-b border-dashed py-1.5 font-mono text-xs last:border-b-0">
                <span>{c.name}</span>
                <span className="text-muted-foreground">{c.type}</span>
              </div>
            ))
          ) : (
            <div className="text-xs text-muted-foreground">No schema available.</div>
          )}
        </div>
      )}

      {showFiles && (
        <div className="border-t bg-background">
          {group.files.length > 8 && (
            <div className="flex items-center gap-2 border-b px-3.5 py-2 text-xs text-muted-foreground">
              <span>{group.files.length} {unit.many} · partitioned by account</span>
              <div className="relative ml-auto">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter files"
                  className="h-7 w-44 pl-7 text-xs"
                  aria-label="Filter files"
                />
              </div>
            </div>
          )}
          <div className="max-h-[264px] overflow-y-auto">
            {filtered.map((f) => (
              <div key={f.file_id} className="flex items-center gap-2.5 border-b px-3.5 py-2 text-xs last:border-b-0">
                <FileText className="h-3.5 w-3.5 flex-none text-muted-foreground" aria-hidden />
                <span className="truncate font-mono">{f.filename}</span>
                <span className="ml-auto whitespace-nowrap text-muted-foreground">
                  {(f.row_count ?? 0).toLocaleString()} rows · {formatBytes(f.size_bytes)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
